import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { L10nHTML } from '@uplift/hooks';
import { Button } from '@uplift/ui-v2';
import { Form, Formik, FormikConfig, FormikProps } from 'formik';
import { useMemo } from 'react';


export type StepFormContainerProps = {
  title: string;
  description?: React.ReactNode;
  form: React.ReactNode;
  onBack?: () => void;
  children?: React.ReactNode;
  nestForm?: boolean;
  requireValidation?: boolean;
  isLoading?: boolean;
} & ({
  onNext: () => void;
} | {
  renderNextButton: () => React.ReactNode;
});

export const StepFormContainer: React.VFC<StepFormContainerProps> = props => {
  const { classes } = useStepFormContainerStyles();

  const nextButton = useMemo(() => {
    if ('onNext' in props) {
      return (
        <Button
          variant="contained"
          color="secondary"
          disabled={props.isLoading}
          onClick={props.onNext}
        >
          Next
        </Button>
      );
    }

    return props.renderNextButton();
  }, [ props ]);


  const description = useMemo(() => {
    if (typeof props.description === 'string') {
      return (
        <L10nHTML color="textSecondary" className={classes.description}>
          {props.description}
        </L10nHTML>
      );
    }

    return props.description;
  }, [ classes.description, props.description ]);

  return (
    <Box display="flex" flexDirection="column" gap="1.5rem">

      <Box>
        <Box mb={1}>
          <Typography variant="h4">{props.title}</Typography>
        </Box>
        {description}
      </Box>

      {props.children}

      {props.form && (
        <Box className={props.nestForm && classes.nestForm}>
          {props.form}
        </Box>
      )}

      <Box mt={4}>
        <Divider />
        <Box display="flex" justifyContent={props.onBack ? 'space-between' : 'flex-end'} p={2}>
          {props.onBack && (
            <Button onClick={props.onBack} disabled={props.isLoading}>
              Back
            </Button>
          )}
          {nextButton}
        </Box>
      </Box>

    </Box>
  );
};

export type StepFormikContainerProps = {
  formikProps: FormikConfig<any>;

  /*
    * If provided, the onClick of the "Next" button will be set to this function and formik will validate on mount.
    * This will bypass the formik's submit, but only if the form is not dirty and is valid
    */
  onSkip?: () => void;

  /*
    * If provided in combination with onSkip, this function will be called to determine if the onClick of the "Next" button will be set to the onSkip function.
    * By default, the form will be skippable if it is not dirty and is valid
    */
  isSkippable?: (formikProps: FormikProps<any>) => boolean;

  disableNextWhenInvalid?: boolean;


  requireValidation?: boolean;
} & Omit<StepFormContainerProps, 'onNext' | 'renderNextButton'>;

export const StepFormikContainer: React.VFC<StepFormikContainerProps> = props => {

  const isSkippable = (formik: FormikProps<any>) => {
    if (props.onSkip) {
      if (props.isSkippable) {
        return props.isSkippable(formik);
      }

      return !formik.dirty && formik.isValid;
    }

    return false;
  };

  return (
    <Formik {...props.formikProps} validationSchema={props.requireValidation ? props.formikProps?.validationSchema : undefined} validateOnMount={!!props.onSkip}>
      {formik => {
        const canSkip = isSkippable(formik);
        const disabledWhenInvalid = props.disableNextWhenInvalid && !formik.isValid;

        return (
          <Form>
            <StepFormContainer
              {...props}
              renderNextButton={() => (
                <Button
                  type={canSkip ? 'button' : 'submit'}
                  variant="contained"
                  color="secondary"
                  disabled={formik.isSubmitting || disabledWhenInvalid}
                  onClick={canSkip ? props.onSkip : undefined}
                >
                  Next
                </Button>
              )}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

const useStepFormContainerStyles = makeStyles({ name: 'StepFormContainer' })(theme => ({
  form: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  description: {
    '& b': {
      fontWeight: 500
    },
  },
  nestForm: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[100],
  },
}));
